import { useMemo } from 'react'
import { CompanyColors } from '@popety-io/types'

interface ThemeColorSource {
  company?: {
    colors?: CompanyColors
  }
  office?: {
    colors?: CompanyColors
    font_family?: string
  }
}

interface ResolvedThemeConfig {
  colors?: {
    primary?: string
    secondary?: string
    accent?: string
    background?: string
    text?: string
  }
  fonts?: {
    primary?: string
    secondary?: string
  }
}

/**
 * Resolves theme colors with hierarchy:
 * 1. Office colors (if office has colors with a primary color)
 * 2. Company colors (if no office colors but company has colors with a primary color)
 * 3. null (use default theme)
 */
export const useResolvedThemeColors = (
  data: ThemeColorSource | null
): ResolvedThemeConfig | null => {
  return useMemo(() => {
    if (!data) return null

    // Priority 1: Office colors
    const officeColors = data.office?.colors
    if (officeColors?.primary) {
      return {
        colors: {
          primary: officeColors.primary,
          secondary: officeColors.secondary,
          accent: officeColors.accent,
          background: officeColors.background,
          text: officeColors.text,
        },
        fonts: data.office?.font_family
          ? {
              primary: data.office.font_family,
              secondary: data.office.font_family,
            }
          : undefined,
      }
    }

    // Priority 2: Company colors
    const companyColors = data.company?.colors
    if (companyColors?.primary) {
      return {
        colors: {
          primary: companyColors.primary,
          secondary: companyColors.secondary,
          accent: companyColors.accent,
          background: companyColors.background,
          text: companyColors.text,
        },
      }
    }

    // No custom colors - use default theme
    return null
  }, [data?.office?.colors, data?.company?.colors, data?.office?.font_family])
}
